/**
 * BookingManagement — Admin Booking Management Page
 *
 * Read-only admin view for browsing, searching, and filtering all platform bookings.
 * Follows the CancellationManagement.jsx / DisputeManagement.jsx pattern.
 *
 * Story: SCRUM-842 (FIX-H05: Admin booking management)
 *
 * AC-01: Paginated list with customer/guide/experience info
 * AC-02: Search by booking number, customer, guide
 * AC-03: Filters: status, date range
 * AC-06: Stats header with counts by status
 * AC-07: Route + nav entry working
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Modal from '../../components/shared/Modal';
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  Filter,
  Search,
  DollarSign,
  Calendar
} from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'paid', label: 'Paid' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'refunded', label: 'Refunded' }
];

const STATUS_BADGES = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-800', icon: DollarSign },
  in_progress: { label: 'In Progress', color: 'bg-indigo-100 text-indigo-800', icon: RefreshCw },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle },
  refunded: { label: 'Refunded', color: 'bg-gray-100 text-gray-800', icon: RefreshCw }
};

// ── Formatting helpers ────────────────────────────────────────────────

const formatCurrency = (cents) => {
  if (cents === null || cents === undefined) { return 'N/A'; }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
};

const formatDate = (dateStr) => {
  if (!dateStr) { return 'N/A'; }
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// ── Component ─────────────────────────────────────────────────────────

export default function BookingManagement() {
  const navigate = useNavigate();

  // Data state
  const [bookings, setBookings] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Detail modal state
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // ── Fetch bookings ────────────────────────────────────────────────

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (searchTerm) { params.set('search', searchTerm); }
      if (statusFilter) { params.set('status', statusFilter); }
      params.set('page', currentPage.toString());
      params.set('limit', '25');

      const response = await apiRequest(`/api/v1/admin/bookings?${params.toString()}`);

      if (response.success) {
        setBookings(response.data.bookings || []);
        setPagination(response.data.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 });
      }
    } catch (err) {
      setError(err.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, currentPage]);

  // ── Fetch stats ───────────────────────────────────────────────────

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiRequest('/api/v1/admin/bookings/stats');
      if (response.success) {
        setStats(response.data || {});
      }
    } catch (err) {
      // Stats failure is non-critical; don't block the page
    }
  }, []);

  // ── Auto-fetch on mount and filter change ─────────────────────────

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // ── Fetch detail ──────────────────────────────────────────────────

  const fetchDetail = async (bookingId) => {
    try {
      setDetailLoading(true);
      const response = await apiRequest(`/api/v1/admin/bookings/${bookingId}`);
      if (response.success) {
        setSelectedBooking(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load booking details');
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Render helpers ────────────────────────────────────────────────

  const renderStatusBadge = (status) => {
    const config = STATUS_BADGES[status] || STATUS_BADGES.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchBookings();
  };

  // ── Render ────────────────────────────────────────────────────────

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="bookings"
      onNavigate={(id, path) => path && navigate(path)}
    >
      <div className="space-y-6">
        {/* HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-900">Booking Management</h1>
          <button
            onClick={() => { fetchBookings(); fetchStats(); }}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-dark disabled:opacity-50 transition text-sm font-medium"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* STATS CARDS (AC-06) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Total</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.pending || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Confirmed</p>
            <p className="text-2xl font-bold text-blue-600">{stats.confirmed || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Completed</p>
            <p className="text-2xl font-bold text-green-600">{stats.completed || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Revenue</p>
            <p className="text-2xl font-bold text-green-700">{formatCurrency(stats.totalRevenueCents)}</p>
          </div>
        </div>

        {/* SEARCH + FILTERS (AC-02, AC-03) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search booking #, customer, guide..."
                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm"
              />
            </form>
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* ERROR */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        {/* TABLE (AC-01) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
              <span className="ml-3 text-gray-600">Loading bookings...</span>
            </div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No bookings found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Booking #</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden sm:table-cell">Customer</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden md:table-cell">Guide</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden lg:table-cell">Experience</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Date</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden sm:table-cell">Amount</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {bookings.map((b) => (
                    <tr key={b.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-gray-600">
                          {b.bookingNumber || b.booking_number || ''}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="text-gray-900">{b.customerName || b.customer_name || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-gray-900">{b.guideName || b.guide_name || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-gray-700 truncate max-w-[200px] block">
                          {b.experienceTitle || b.experience_title || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        {formatDate(b.startDate || b.start_date)}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="font-medium text-green-600">
                          {formatCurrency(b.totalPriceCents || b.total_price_cents)}
                        </span>
                      </td>
                      <td className="px-4 py-3">{renderStatusBadge(b.status)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => fetchDetail(b.id)}
                          className="inline-flex items-center gap-1 text-brand-primary hover:text-brand-dark text-xs font-medium"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* PAGINATION */}
          {pagination.totalPages > 1 && (
            <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  className="p-1.5 rounded border border-gray-300 disabled:opacity-30"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                  disabled={currentPage >= pagination.totalPages}
                  className="p-1.5 rounded border border-gray-300 disabled:opacity-30"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* DETAIL MODAL (AC-04, AC-05) */}
        <Modal
          isOpen={!!selectedBooking}
          onClose={() => setSelectedBooking(null)}
          title="Booking Details"
          footer={
            <button
              onClick={() => setSelectedBooking(null)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
            >
              Close
            </button>
          }
        >
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
            </div>
          ) : selectedBooking ? (
            <div className="space-y-6">
              {/* Booking Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Booking Info</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Booking #:</span> <span className="font-mono">{selectedBooking.bookingNumber}</span></div>
                  <div><span className="text-gray-500">Status:</span> {renderStatusBadge(selectedBooking.status)}</div>
                  <div><span className="text-gray-500">Date:</span> {formatDate(selectedBooking.startDate)}{selectedBooking.durationDays > 1 ? ` – ${formatDate(selectedBooking.endDate)}` : ''}</div>
                  <div><span className="text-gray-500">Participants:</span> {selectedBooking.participantCount}</div>
                  <div><span className="text-gray-500">Created:</span> {formatDate(selectedBooking.createdAt)}</div>
                  {selectedBooking.durationDays > 1 && <div><span className="text-gray-500">Duration:</span> {selectedBooking.durationDays} days</div>}
                </div>
              </div>

              {/* Parties */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Parties</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium text-gray-800">Customer</p>
                    <p>{selectedBooking.customer?.name || 'N/A'}</p>
                    <p className="text-gray-500">{selectedBooking.customer?.email}</p>
                    {selectedBooking.customer?.phone && <p className="text-gray-500">{selectedBooking.customer.phone}</p>}
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Guide</p>
                    <p>{selectedBooking.guide?.name || 'N/A'}</p>
                    <p className="text-gray-500">{selectedBooking.guide?.email}</p>
                    {selectedBooking.guide?.businessName && <p className="text-gray-500">{selectedBooking.guide.businessName}</p>}
                  </div>
                </div>
                {selectedBooking.experience?.title && (
                  <div className="mt-2 text-sm">
                    <span className="text-gray-500">Experience:</span> {selectedBooking.experience.title}
                  </div>
                )}
              </div>

              {/* Financial Summary */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Financial Summary</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Base Price:</span> {formatCurrency(selectedBooking.basePriceCents)}</div>
                  <div><span className="text-gray-500">Additional Fees:</span> {formatCurrency(selectedBooking.additionalFeesCents)}</div>
                  <div><span className="text-gray-500">Discounts:</span> {formatCurrency(selectedBooking.discountCents)}</div>
                  <div><span className="text-gray-500">Platform Fee:</span> {formatCurrency(selectedBooking.platformFeeCents)}</div>
                  <div className="col-span-2 border-t pt-1 mt-1">
                    <span className="text-gray-700 font-medium">Total:</span>{' '}
                    <span className="font-bold text-green-700">{formatCurrency(selectedBooking.totalPriceCents)}</span>
                  </div>
                  {selectedBooking.depositAmountCents > 0 && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Deposit:</span> {formatCurrency(selectedBooking.depositAmountCents)}
                      {selectedBooking.depositPaidAt && <span className="text-green-600 ml-1">(paid)</span>}
                    </div>
                  )}
                </div>
              </div>

              {/* Ledger Entries */}
              {selectedBooking.ledgerEntries && selectedBooking.ledgerEntries.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Ledger Entries</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-1 text-left text-gray-500">Type</th>
                          <th className="px-2 py-1 text-left text-gray-500">Description</th>
                          <th className="px-2 py-1 text-right text-gray-500">Amount</th>
                          <th className="px-2 py-1 text-left text-gray-500">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {selectedBooking.ledgerEntries.map((le) => (
                          <tr key={le.id}>
                            <td className="px-2 py-1 capitalize">{le.entryType}</td>
                            <td className="px-2 py-1 text-gray-600">{le.description}</td>
                            <td className="px-2 py-1 text-right font-medium">{formatCurrency(le.amountCents)}</td>
                            <td className="px-2 py-1 text-gray-500">{formatDate(le.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Payment Schedule */}
              {selectedBooking.paymentSchedule && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Payment Schedule</h3>
                  <p className="text-xs text-gray-500 mb-1">
                    Type: {selectedBooking.paymentSchedule.scheduleType} | Status: {selectedBooking.paymentSchedule.status}
                  </p>
                  {selectedBooking.paymentSchedule.installments && selectedBooking.paymentSchedule.installments.length > 0 && (
                    <div className="space-y-1">
                      {selectedBooking.paymentSchedule.installments.map((inst) => (
                        <div key={inst.id} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1">
                          <span>Installment #{inst.installmentNumber}</span>
                          <span className="font-medium">{formatCurrency(inst.amountCents)}</span>
                          <span className={inst.status === 'paid' ? 'text-green-600' : 'text-yellow-600'}>{inst.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Related Records (AC-05) */}
              {selectedBooking.relatedRecords && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Related Records</h3>
                  <div className="space-y-1 text-sm">
                    {selectedBooking.relatedRecords.cancellation && (
                      <div className="flex items-center justify-between bg-red-50 rounded px-3 py-2">
                        <span>Cancellation ({selectedBooking.relatedRecords.cancellation.status})</span>
                        <button
                          onClick={() => { setSelectedBooking(null); navigate('/admin/cancellations'); }}
                          className="text-xs text-brand-primary hover:text-brand-dark font-medium"
                        >
                          View in Cancellations &rarr;
                        </button>
                      </div>
                    )}
                    {selectedBooking.relatedRecords.dispute && (
                      <div className="flex items-center justify-between bg-yellow-50 rounded px-3 py-2">
                        <span>Dispute ({selectedBooking.relatedRecords.dispute.status})</span>
                        <button
                          onClick={() => { setSelectedBooking(null); navigate('/admin/disputes'); }}
                          className="text-xs text-brand-primary hover:text-brand-dark font-medium"
                        >
                          View in Disputes &rarr;
                        </button>
                      </div>
                    )}
                    {selectedBooking.relatedRecords.refunds && selectedBooking.relatedRecords.refunds.length > 0 && (
                      <div className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                        <span>{selectedBooking.relatedRecords.refunds.length} Refund(s) — {formatCurrency(selectedBooking.relatedRecords.refunds.reduce((sum, r) => sum + (r.amountCents || 0), 0))}</span>
                        <button
                          onClick={() => { setSelectedBooking(null); navigate('/admin/refunds'); }}
                          className="text-xs text-brand-primary hover:text-brand-dark font-medium"
                        >
                          View in Refunds &rarr;
                        </button>
                      </div>
                    )}
                    {!selectedBooking.relatedRecords.cancellation && !selectedBooking.relatedRecords.dispute && (!selectedBooking.relatedRecords.refunds || selectedBooking.relatedRecords.refunds.length === 0) && (
                      <p className="text-gray-400 text-xs">No related cancellations, disputes, or refunds.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {(selectedBooking.customerNotes || selectedBooking.specialRequirements || selectedBooking.guideNotes) && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes</h3>
                  {selectedBooking.customerNotes && (
                    <div className="text-sm mb-1"><span className="text-gray-500">Customer:</span> {selectedBooking.customerNotes}</div>
                  )}
                  {selectedBooking.specialRequirements && (
                    <div className="text-sm mb-1"><span className="text-gray-500">Special Requirements:</span> {selectedBooking.specialRequirements}</div>
                  )}
                  {selectedBooking.guideNotes && (
                    <div className="text-sm"><span className="text-gray-500">Guide:</span> {selectedBooking.guideNotes}</div>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </Modal>
      </div>
    </DashboardLayout>
  );
}
